import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  MessagesSquare,
  Users,
  BookOpen,
  Settings,
  LogOut,
  Shield,
  Zap,
  UserCircle,
  Menu,
  Mail,
  Key,
  Brain,
  UserCog,
  Building2,
  ChevronDown,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useState, useEffect, useCallback } from 'react';
import { getApiBase } from '../../lib/utils';
import { api } from '../../lib/api';
import { useAppLanguage } from '../../i18n';

const ADVANCED_PATH_RE = /^\/(memory|end-users|workflows|email)(\/|$)/;

function readAdvancedOpenInitial(): boolean {
  try {
    const s = localStorage.getItem('sidebar_advanced_open');
    if (s !== null) return s === 'true';
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined') {
    return ADVANCED_PATH_RE.test(window.location.pathname);
  }
  return false;
}

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  badge?: string;
  badgeTone?: 'preview' | 'beta';
}

interface SidebarProps {
  onLogout?: () => void;
  onCollapsedChange?: (collapsed: boolean) => void;
  version?: string;
}

export function Sidebar({ onLogout, onCollapsedChange, version }: SidebarProps) {
  const { t } = useAppLanguage();
  const { pathname } = useLocation();
  const [branding, setBranding] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAgency, setIsAgency] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [advancedOpen, setAdvancedOpen] = useState(readAdvancedOpenInitial);

  useEffect(() => {
    fetch(`${getApiBase()}/global-config/public`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) {
          setBranding({
            logo_url: data.logo_url ? `${getApiBase()}${data.logo_url}` : null,
            site_name: data.site_name || 'Totemium',
          });
        }
      })
      .catch(() => {});

    api
      .get('/fapi/users/me')
      .then((res) => {
        setIsAdmin(!!res.data?.is_superuser);
        if (res.data?.id) {
          api
            .get('/billing/subscription')
            .then((subRes) => {
              const isAgencyPlan = subRes.data?.plan?.max_workspaces > 1;
              setIsAgency(isAgencyPlan);
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (ADVANCED_PATH_RE.test(pathname)) {
      setAdvancedOpen(true);
    }
  }, [pathname]);

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    try {
      localStorage.setItem('sidebar_collapsed', String(newState));
    } catch {
      /* ignore */
    }
  };

  const toggleAdvanced = useCallback(() => {
    setAdvancedOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('sidebar_advanced_open', String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    onCollapsedChange?.(isCollapsed);
  }, [isCollapsed, onCollapsedChange]);

  const overviewItems: NavItem[] = [
    { name: t.sidebar.dashboard, href: '/', icon: LayoutDashboard },
    ...(isAgency
      ? [{ name: t.sidebar.agencyDashboard, href: '/agency/dashboard', icon: Building2 }]
      : []),
  ];

  const knowledgesItems: NavItem[] = [
    { name: t.sidebar.knowledges, href: '/knowledges', icon: BookOpen },
  ];

  const agentsItems: NavItem[] = [
    { name: t.sidebar.agents, href: '/agents', icon: Users },
    { name: t.sidebar.messenger, href: '/messenger', icon: MessagesSquare },
  ];

  const advancedItems: NavItem[] = [
    { name: t.sidebar.memory, href: '/memory', icon: Brain, badge: t.sidebar.badgePreview, badgeTone: 'preview' },
    {
      name: t.sidebar.endUsers,
      href: '/end-users',
      icon: UserCog,
      badge: t.sidebar.badgePreview,
      badgeTone: 'preview',
    },
    { name: t.sidebar.workflows, href: '/workflows', icon: Zap, badge: t.sidebar.badgeBeta, badgeTone: 'beta' },
    { name: t.sidebar.email, href: '/email', icon: Mail, badge: t.sidebar.badgePreview, badgeTone: 'preview' },
  ];

  const secondaryNavigation = [
    { name: t.sidebar.admin, href: '/admin', icon: Shield, adminOnly: true },
    ...(isAgency ? [{ name: t.sidebar.sharedApiKeys, href: '/agency/byok', icon: Key }] : []),
    { name: t.sidebar.configuration, href: '/config', icon: Settings },
    { name: t.sidebar.myAccount, href: '/account', icon: UserCircle },
  ];

  const renderNavLink = (
    item: NavItem,
    opts: { collapsed: boolean; subtle?: boolean }
  ) => (
    <li key={`${item.href}-${item.name}`}>
      <NavLink
        to={item.href}
        className={({ isActive }) =>
          cn(
            'group flex gap-x-3 rounded-lg p-2 font-medium leading-6 transition-all duration-200',
            opts.subtle ? 'text-xs text-zinc-500' : 'text-sm',
            isActive
              ? 'bg-emerald-500/10 text-emerald-400 shadow-[inset_3px_0_0_0_#10b981]'
              : cn(
                  'text-zinc-400 hover:bg-white/5 hover:text-white',
                  opts.subtle && 'opacity-80 hover:opacity-100'
                ),
            opts.collapsed && 'justify-center',
            opts.subtle && !opts.collapsed && 'py-1.5'
          )
        }
        title={opts.collapsed ? item.name : undefined}
      >
        <item.icon className={cn('h-5 w-5 shrink-0', opts.subtle && 'h-4 w-4')} aria-hidden="true" />
        {!opts.collapsed && (
          <>
            <span className="flex-1 truncate">{item.name}</span>
            {item.badge && (
              <span
                className={cn(
                  'ml-auto shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
                  item.badgeTone === 'beta'
                    ? 'bg-amber-500/15 text-amber-400/90'
                    : 'bg-zinc-500/20 text-zinc-400'
                )}
              >
                {item.badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    </li>
  );

  return (
    <div
      className={cn(
        'flex h-full flex-col bg-zinc-950 border-r border-white/5 transition-all duration-300',
        isCollapsed ? 'w-20' : 'w-72'
      )}
    >
      <div className="flex h-16 shrink-0 items-center gap-3 px-6 border-b border-white/5">
        {isCollapsed ? (
          <button
            onClick={toggleSidebar}
            className="h-8 w-8 rounded bg-emerald-500/10 hover:bg-emerald-500/20 flex items-center justify-center transition-colors"
            aria-label={t.sidebar.openMenu}
          >
            <Menu className="h-5 w-5 text-emerald-400" />
          </button>
        ) : (
          <>
            <button
              onClick={toggleSidebar}
              className="h-8 w-8 rounded hover:bg-white/5 flex items-center justify-center transition-colors"
              aria-label={t.sidebar.closeMenu}
            >
              <Menu className="h-5 w-5 text-zinc-400 hover:text-white" />
            </button>
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-8 w-8 object-contain" />
            ) : (
              <div className="h-8 w-8 rounded bg-emerald-500 flex items-center justify-center">
                <LayoutDashboard className="h-5 w-5 text-white" />
              </div>
            )}
            <span className="font-bold text-lg text-white tracking-tight">
              {branding?.site_name || 'Totemium'}
            </span>
          </>
        )}
      </div>

      <nav className="flex flex-1 flex-col px-4 py-5 gap-y-6 overflow-y-auto min-h-0">
        {isCollapsed ? (
          <div className="flex flex-col gap-y-1">
            <ul className="space-y-1">{overviewItems.map((i) => renderNavLink(i, { collapsed: true }))}</ul>
            <div className="my-1 border-t border-white/5" />
            <ul className="space-y-1">{knowledgesItems.map((i) => renderNavLink(i, { collapsed: true }))}</ul>
            <div className="my-1 border-t border-white/5" />
            <ul className="space-y-1">{agentsItems.map((i) => renderNavLink(i, { collapsed: true }))}</ul>
            <div className="my-1 border-t border-white/5 opacity-60" />
            <ul className="space-y-1">{advancedItems.map((i) => renderNavLink(i, { collapsed: true, subtle: true }))}</ul>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-y-1">
              <div className="text-[10px] font-semibold leading-6 text-zinc-500 px-2 mb-1 uppercase tracking-wider">
                {t.sidebar.sectionOverview}
              </div>
              <ul className="space-y-1">{overviewItems.map((i) => renderNavLink(i, { collapsed: false }))}</ul>
            </div>

            <div className="flex flex-col gap-y-1">
              <div className="text-[10px] font-semibold leading-6 text-zinc-500 px-2 mb-1 uppercase tracking-wider">
                {t.sidebar.sectionKnowledges}
              </div>
              <ul className="space-y-1">{knowledgesItems.map((i) => renderNavLink(i, { collapsed: false }))}</ul>
            </div>

            <div className="flex flex-col gap-y-1">
              <div className="text-[10px] font-semibold leading-6 text-zinc-500 px-2 mb-1 uppercase tracking-wider">
                {t.sidebar.sectionAgentsDiscussions}
              </div>
              <ul className="space-y-1">{agentsItems.map((i) => renderNavLink(i, { collapsed: false }))}</ul>
            </div>

            <div className="flex flex-col gap-y-0.5 rounded-lg border border-white/5 bg-white/[0.02] p-1.5">
              <button
                type="button"
                onClick={toggleAdvanced}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500 hover:bg-white/5 hover:text-zinc-400 transition-colors"
                aria-expanded={advancedOpen}
              >
                <span className="flex-1">{t.sidebar.sectionAdvanced}</span>
                <ChevronDown
                  className={cn('h-3.5 w-3.5 shrink-0 text-zinc-500 transition-transform', advancedOpen && 'rotate-180')}
                  aria-hidden
                />
                <span className="sr-only">{advancedOpen ? t.sidebar.advancedLess : t.sidebar.advancedMore}</span>
              </button>
              {advancedOpen && (
                <ul className="space-y-0.5 border-t border-white/5 pt-1 mt-0.5">
                  {advancedItems.map((i) => renderNavLink(i, { collapsed: false, subtle: true }))}
                </ul>
              )}
            </div>
          </>
        )}

        <div className="mt-auto pt-2">
          {!isCollapsed && (
            <div className="text-[10px] font-semibold leading-6 text-zinc-500 px-2 mb-2 uppercase tracking-wider">
              {t.sidebar.system}
            </div>
          )}
          <ul role="list" className="space-y-1">
            {secondaryNavigation.map((item) => {
              if (item.adminOnly && !isAdmin) return null;
              return (
                <li key={item.name}>
                  <NavLink
                    to={item.href}
                    className={({ isActive }) =>
                      cn(
                        'group flex gap-x-3 rounded-lg p-2 text-sm font-medium leading-6 transition-all duration-200',
                        isActive
                          ? item.adminOnly
                            ? 'bg-violet-500/10 text-violet-400 shadow-[inset_3px_0_0_0_#8b5cf6]'
                            : 'bg-emerald-500/10 text-emerald-400'
                          : item.adminOnly
                            ? 'text-zinc-400 hover:bg-violet-500/10 hover:text-violet-400'
                            : 'text-zinc-400 hover:bg-white/5 hover:text-white',
                        isCollapsed && 'justify-center'
                      )
                    }
                    title={isCollapsed ? item.name : undefined}
                  >
                    <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    {!isCollapsed && item.name}
                  </NavLink>
                </li>
              );
            })}
            <li>
              <button
                className={cn(
                  'w-full group flex gap-x-3 rounded-lg p-2 text-sm font-medium leading-6 text-zinc-400 hover:bg-red-500/10 hover:text-red-400 transition-all',
                  isCollapsed && 'justify-center'
                )}
                onClick={onLogout}
                title={isCollapsed ? t.sidebar.logout : undefined}
              >
                <LogOut className="h-5 w-5 shrink-0" aria-hidden="true" />
                {!isCollapsed && t.sidebar.logout}
              </button>
            </li>
          </ul>
        </div>

        {!isCollapsed && (
          <div className="px-1 pb-2 pt-2 border-t border-white/5 flex items-center justify-between gap-2 shrink-0">
            <p className="text-[10px] text-zinc-600 leading-none shrink-0">© {new Date().getFullYear()} Totemium</p>
            {version && (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-emerald-400 shrink-0">
                <span className="h-1 w-1 rounded-full bg-emerald-400 animate-pulse" />
                v{version}
              </span>
            )}
          </div>
        )}
      </nav>
    </div>
  );
}
